import React, { useState, useEffect } from "react";
import {
  getCountries,
  getCountryCallingCode,
  CountryCode,
  isValidPhoneNumber,
} from "libphonenumber-js";
import { useTranslation } from "react-i18next";

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  id: string;
  required?: boolean;
  placeholder?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  label,
  id,
  required = false,
  placeholder = "612345678",
}) => {
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState<CountryCode | null>(
    "NL" // Default to Netherlands
  );
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const countries = getCountries();

  // Get country name using Intl.DisplayNames
  const getCountryName = (countryCode: CountryCode): string => {
    try {
      return (
        new Intl.DisplayNames(["en"], { type: "region" }).of(countryCode) ||
        countryCode
      );
    } catch {
      return countryCode;
    }
  };

  // Create countries with names and codes
  const countriesWithNames = countries.map((code) => ({
    code,
    name: getCountryName(code),
    callingCode: getCountryCallingCode(code),
  }));

  // Filter countries based on search
  const filteredCountries = countriesWithNames.filter((country) => {
    const search = searchTerm.toLowerCase();
    return (
      country.name.toLowerCase().includes(search) ||
      country.callingCode.includes(search) ||
      country.code.toLowerCase().includes(search)
    );
  });

  // Parse existing value on mount
  useEffect(() => {
    if (value) {
      // Simple parsing for existing values - just extract number part
      if (value.startsWith("+")) {
        const numberPart = value.substring(1);
        // Find which country this belongs to
        const matchingCountry = countries.find((country) => {
          const countryCode = getCountryCallingCode(country);
          return numberPart.startsWith(countryCode);
        });

        if (matchingCountry) {
          setSelectedCountry(matchingCountry);
          const countryCode = getCountryCallingCode(matchingCountry);
          setPhoneNumber(numberPart.substring(countryCode.length));
        } else {
          // Default to NL if can't determine
          setSelectedCountry("NL");
          setPhoneNumber(numberPart.substring(2)); // Remove "31"
        }
      } else {
        // Not a full international number, just set as national number
        setPhoneNumber(value);
      }
    }
  }, [value, countries]);

  const handleCountrySelect = (country: {
    code: CountryCode;
    name: string;
    callingCode: string;
  }) => {
    setSelectedCountry(country.code);
    setSearchTerm("");
    setIsDropdownOpen(false);
    updateFullNumber(country.code, phoneNumber);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    setIsDropdownOpen(true);

    // If user clears the input, reset selected country
    if (!inputValue) {
      setSelectedCountry(null);
      onChange("");
      return;
    }

    // Check if user typed a country code manually (like +31 or 31)
    const cleanInput = inputValue.replace("+", "");
    if (/^\d+$/.test(cleanInput)) {
      // Find country by calling code
      const matchingCountry = countriesWithNames.find(
        (country) => country.callingCode === cleanInput
      );
      if (matchingCountry) {
        setSelectedCountry(matchingCountry.code);
        setSearchTerm("");
        setIsDropdownOpen(false);
        updateFullNumber(matchingCountry.code, phoneNumber);
      }
    }
  };

  const handleInputClick = () => {
    setIsDropdownOpen(true);
  };

  const handlePhoneChange = (number: string) => {
    // Only allow digits, spaces, dashes, parentheses
    const cleanNumber = number.replace(/[^\d\s\-()]/g, "");
    setPhoneNumber(cleanNumber);
    if (selectedCountry) {
      updateFullNumber(selectedCountry, cleanNumber);
    } else if (cleanNumber) {
      // Default to Netherlands if no country selected but number provided
      setSelectedCountry("NL");
      updateFullNumber("NL", cleanNumber);
    }
  };

  const updateFullNumber = (country: CountryCode, number: string) => {
    if (!number.trim()) {
      onChange("");
      setIsValid(true);
      return;
    }

    try {
      const countryCode = getCountryCallingCode(country);
      const fullNumber = `+${countryCode}${number.replace(/\D/g, "")}`;

      // Validate the phone number
      const valid = isValidPhoneNumber(fullNumber, country);
      setIsValid(valid);

      // Always send the formatted number to parent, even if invalid
      onChange(fullNumber);
    } catch {
      setIsValid(false);
      onChange(
        `+${getCountryCallingCode(country)}${number.replace(/\D/g, "")}`
      );
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="flex">
        {/* Searchable Country Input */}
        <div className="relative">
          <input
            type="text"
            value={
              searchTerm ||
              (selectedCountry
                ? `+${getCountryCallingCode(selectedCountry)}`
                : "")
            }
            onChange={handleInputChange}
            onClick={handleInputClick}
            className="w-16 px-2 py-3 border border-gray-600 rounded-l-md focus:outline-hidden focus:ring-2 focus:ring-(--primary) bg-gray-700 text-white text-sm"
            placeholder={t("1173", "Code")}
          />

          {/* Dropdown */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 w-80 max-h-80 overflow-y-auto bg-gray-800 border border-gray-600 rounded-md shadow-lg z-10">
              {filteredCountries.map((country) => (
                <div
                  key={country.code}
                  onClick={() => handleCountrySelect(country)}
                  className="px-3 py-2 hover:bg-gray-700 cursor-pointer text-sm flex justify-between text-white"
                >
                  <span>{country.name}</span>
                  <span className="text-gray-400">+{country.callingCode}</span>
                </div>
              ))}
              {filteredCountries.length === 0 && (
                <div className="px-3 py-2 text-gray-400 text-sm">
                  {t("1174", "No countries found")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <input
          type="tel"
          id={id}
          value={phoneNumber}
          onChange={(e) => handlePhoneChange(e.target.value)}
          placeholder={placeholder}
          className={`flex-1 px-3 py-2 border-t border-r border-b border-gray-600 bg-gray-700 text-white rounded-r-md focus:outline-hidden focus:ring-2 focus:ring-(--primary) ${
            !isValid && phoneNumber ? "border-red-300 bg-red-50" : ""
          }`}
          required={required}
        />
      </div>

      {/* Validation Message */}
      {!isValid && phoneNumber && (
        <p className="text-xs text-red-600 mt-1">
          {t("1175", "Please enter a valid phone number")}
        </p>
      )}

      {/* Preview of formatted number */}
      {phoneNumber && isValid && selectedCountry && (
        <p className="text-xs text-gray-400 mt-1">
          {t("1176", "Format")}: +{getCountryCallingCode(selectedCountry)}
          {phoneNumber.replace(/\D/g, "")}
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
